import { useEffect, useRef, useState } from "react";
import {
  ContentfulContentType,
  ContentfulKey,
  ContentSection,
  GenericMarketingPage,
  PageSection,
  PageSectionKey,
  PdfDownloadType,
} from "@tiicker/util/lib/contentful/types";
import { GetStaticProps } from "next";
import { getContentfulClient } from "@tiicker/util/lib/contentful/client";
import { Entry, EntryCollection } from "contentful";
import { compact } from "lodash";
import {
  ContentSections,
  getItemReference,
  loadContentSections,
} from "@tiicker/util/lib/contentful/helpers";
import styles from "./partner.module.scss";
import { useRouter } from "next/router";
import { RecordLeadGenerationLog } from "$gql/mutations/general/RecordLeadGenerationLog.gen";
import { useMutationBundle } from "source/hooks";
import { triggerLeadVisitedEvent } from "source/analytics-events";
import GenericMetaTags from "components/MetaTags/GenericMetaTags";
import ColorWrapper from "components/Home/ColorWrapper/ColorWrapper";
import NavigationBar from "components/Global/Navigation/NavigationBar/NavigationBar";
import Container from "components/core/Layout/Container/Container";
import PartnerWithUsStatsSection from "components/PartnerWithUs/PartnerWithUsStatsSection";
import FooterV2 from "components/FooterV2/FooterV2";
import PartnerWithUsRewardSection from "components/PartnerWithUs/V2/PartnerWithUsRewardSection";
import PartnerWithUsFaqSection from "components/PartnerWithUs/V2/PartnerWithUsFaqSection";
import PartnerWithUsShareholderSection from "components/PartnerWithUs/V2/PartnerWithUsShareholderSection";
import PartnerWithUsShareholderSubSection from "components/PartnerWithUs/V2/PartnerWithUsShareholderSubSection";
import PartnerWithUsTopSection from "components/PartnerWithUs/V2/PartnerWithUsTopSection";
import PartnerWithUsTestimonialSection from "components/PartnerWithUs/V2/PartnerWithUsTestimonialSection";
import CalendlyEmbed from "components/Calendly/CalendlyEmbed";
import Modal from "components/Modal/Modal";
import WhitepaperForm from "components/WhitepaperForm/WhitepaperForm";

interface Props {
  partnerWithUsPage: EntryCollection<GenericMarketingPage> | null;
  pageRecord: Entry<GenericMarketingPage> | null;
  pageSections: Entry<PageSection>[];
  contentSections: ContentSections;
}

interface ExtendedWindow extends Window {
  _linkedin_partner_id: string;
  _linkedin_data_partner_ids: string[];
  lintrk: ((a: string, b: any) => void) & { q?: any[] };
}

const PartnerWithUs = (props: Props) => {
  const router = useRouter();
  const [recordLeadGenerationLog] = useMutationBundle(RecordLeadGenerationLog);
  const targetRef = useRef<HTMLDivElement>(null);
  const targetRefOther = useRef<HTMLDivElement>(null);
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState({
    cmo: false,
    ceo: false,
    harris: false,
  });

  const scrollToTarget = () => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleButtonClick = () => {
    setIsModalVisible(true);
  };

  const loadLinkedinScript = () => {
    const myWindow = window as unknown as ExtendedWindow;
    const insertScript = (src) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    };

    // First script
    myWindow._linkedin_partner_id = "1751578";
    myWindow._linkedin_data_partner_ids =
      myWindow._linkedin_data_partner_ids || [];
    myWindow._linkedin_data_partner_ids.push(myWindow._linkedin_partner_id);

    // Second script
    if (!myWindow.lintrk) {
      myWindow.lintrk = function (a, b) {
        myWindow.lintrk.q?.push([a, b]);
      };
      myWindow.lintrk.q = [];
    }

    insertScript("https://snap.licdn.com/li.lms-analytics/insight.min.js");
  };

  useEffect(() => {
    const leadId = router.query?.partnerId;
    if (leadId && !Array.isArray(leadId)) {
      recordLeadGenerationLog({
        variables: {
          leadId,
        },
      }).then((result) => {
        const companyTicker =
          result?.data?.recordLeadGenerationLog?.companyTicker;
        if (companyTicker) {
          triggerLeadVisitedEvent(companyTicker);
        }
      });
    }
  }, [router.query]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setIsStatsVisible(true);
          } else if (
            !entry.isIntersecting &&
            entry.boundingClientRect.top > 0
          ) {
            setIsStatsVisible(false);
          }
        });
      },
      {
        threshold: [0, 0.5, 1],
      }
    );

    if (targetRefOther.current) {
      observer.observe(targetRefOther.current);
    }

    return () => {
      if (targetRefOther.current) {
        observer.unobserve(targetRefOther.current);
      }
    };
  }, []);

  useEffect(() => {
    const myWindow = window as unknown as ExtendedWindow;
    if (!myWindow.lintrk) {
      loadLinkedinScript();
    }
  }, []);

  const findSection = (
    sectionKey: PageSectionKey
  ): Entry<PageSection> | undefined => {
    return props.pageSections.find(
      (section: Entry<PageSection>): boolean =>
        section.fields.sectionKey === sectionKey
    );
  };

  const getContentSections = (
    section: Entry<PageSection> | undefined
  ): Entry<ContentSection>[] => {
    return section ? props.contentSections.sections[section.sys.id] : [];
  };

  const handleFormSuccess = () => {
    const myWindow = window as unknown as ExtendedWindow;
    myWindow.lintrk("track", { conversion_id: 12175578 });
  };

  const triggerPDFDownload = (buttonType: string) => {
    setLoadingStatus((prevStatus) => ({
      ...prevStatus,
      [buttonType.toLowerCase()]: true,
    }));
    const assets = props.partnerWithUsPage?.items[0]?.fields
      ?.pdfDownloads as PdfDownloadType[];

    const pdfAsset = assets?.find((asset) => {
      return asset.fields.title
        .toLowerCase()
        .includes(buttonType.toLowerCase());
    });

    if (pdfAsset) {
      downloadPDF(pdfAsset.fields.file.url, buttonType);
    }
  };

  const downloadPDF = (url: string, buttonType: string) => {
    const link = document.createElement("a");
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setLoadingStatus((prevStatus) => ({
      ...prevStatus,
      [buttonType.toLowerCase()]: false,
    }));
  };

  const topSection: Entry<PageSection> | undefined = findSection(
    PageSectionKey.PartnerWithUsTopV2
  );
  const shareholderSection: Entry<PageSection> | undefined = findSection(
    PageSectionKey.PartnerWIthUsShareholderV2
  );
  const statsSection: Entry<PageSection> | undefined = findSection(
    PageSectionKey.PartnerWithUsStatsV2
  );
  const testimonialSection: Entry<PageSection> | undefined = findSection(
    PageSectionKey.PartnerWithUsTestimonialV2
  );
  const faqSection: Entry<PageSection> | undefined = findSection(
    PageSectionKey.PartnerWithUsFAQV2
  );
  const rewardSection: Entry<PageSection> | undefined = findSection(
    PageSectionKey.PartnerWIthUsRewardV2
  );
  const shareHolderSubSection: Entry<PageSection> | undefined = findSection(
    PageSectionKey.PartnerWithUsSubsectionV2
  );

  const topContentSections: Entry<ContentSection>[] =
    getContentSections(topSection);
  const shareholderContentSections: Entry<ContentSection>[] =
    getContentSections(shareholderSection);
  const statsContentSections: Entry<ContentSection>[] =
    getContentSections(statsSection);
  const testimonialContentSections: Entry<ContentSection>[] =
    getContentSections(testimonialSection);
  const faqContentSections: Entry<ContentSection>[] =
    getContentSections(faqSection);
  const shareHolderSubSectionContentSections: Entry<ContentSection>[] =
    getContentSections(shareHolderSubSection);

  return (
    <>
      {props.pageRecord && (
        <GenericMetaTags pageContent={props.pageRecord.fields} />
      )}
      <ColorWrapper>
        <NavigationBar />

        <Container>
          {topSection && topContentSections && (
            <div className={styles.PartnerWithUs__section}>
              <PartnerWithUsTopSection
                section={topSection}
                contentSections={topContentSections}
                includes={props.partnerWithUsPage?.includes}
                ref={targetRef}
                onButtonClick={handleButtonClick}
              />
            </div>
          )}
        </Container>

        <Container>
          {shareholderSection && shareholderContentSections && (
            <div className={styles.PartnerWithUs__section}>
              <PartnerWithUsShareholderSection
                shareholderSection={shareholderSection}
                contentSections={shareholderContentSections}
                includes={props.partnerWithUsPage?.includes}
                onButtonClick={handleButtonClick}
              />
            </div>
          )}
          {statsContentSections && (
            <div className={styles.PartnerWithUs__section}>
              <PartnerWithUsStatsSection
                title={statsSection?.fields?.displayTitle}
                contentSections={statsContentSections}
                includes={props.partnerWithUsPage?.includes}
                ref={targetRefOther}
              />
            </div>
          )}
        </Container>
      </ColorWrapper>
      <Container>
        {shareHolderSubSection && shareHolderSubSectionContentSections && (
          <div>
            <PartnerWithUsShareholderSubSection
              shareHolderSubSection={shareHolderSubSection}
              contentSections={shareHolderSubSectionContentSections}
            />
          </div>
        )}
      </Container>
      <Container>
        {testimonialContentSections && (
          <div className={styles.PartnerWithUs__section}>
            <PartnerWithUsTestimonialSection
              contentSections={testimonialContentSections}
              includes={props.partnerWithUsPage?.includes}
            />
          </div>
        )}
      </Container>
      <Container>
        {faqContentSections && (
          <div className={styles.PartnerWithUs__section}>
            <PartnerWithUsFaqSection
              title={faqSection?.fields?.displayTitle}
              subTitle={faqSection?.fields?.description}
              contentSections={faqContentSections}
              includes={props.partnerWithUsPage?.includes}
            />
          </div>
        )}
      </Container>
      {rewardSection && (
        <div>
          <PartnerWithUsRewardSection
            section={rewardSection}
            onButtonClick={scrollToTarget}
          />
        </div>
      )}
      {isStatsVisible && (
        <CalendlyEmbed url={"https://calendly.com/tiickerperks/30min"} />
      )}
      <Modal
        visible={isModalVisible}
        width={644}
        onClose={() => setIsModalVisible(false)}
        className={styles.PartnerWithUs__modal}
      >
        <WhitepaperForm
          loadingStatus={loadingStatus}
          onSuccess={triggerPDFDownload}
        />
      </Modal>
      <FooterV2 />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const client = getContentfulClient("contentful");

  const partnerWithUsPage = await client.getEntries<GenericMarketingPage>({
    content_type: ContentfulContentType.GenericMarketingPage,
    ["fields.key"]: ContentfulKey.PartnerWithUs,
  });
  
  const pageRecord =
    partnerWithUsPage.items.length > 0 ? partnerWithUsPage.items[0] : null;

  const pageSections = compact(
    pageRecord?.fields.pageSections?.map((section) =>
      getItemReference(section, partnerWithUsPage.includes)
    )
  );
  const partnerWithUsContent = partnerWithUsPage.items[0];

  const contentSections = await loadContentSections(client, pageSections);
  const title = partnerWithUsContent.fields.displayTitle
  const description =  partnerWithUsContent.fields.displayDescription
  return {
    props: {
      partnerWithUsPage,
      pageRecord,
      pageSections,
      contentSections,
      title, 
      description
    },
    revalidate: 100,
  };
};

export default PartnerWithUs;
